/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import React, { memo } from 'react';
import { SidebarBoundary } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/ui/sidebar-boundary/index.tsx';
import { SidebarContent } from '@jira/navigation-apps__sidebar-nav4-sidebars-settings-global-common/src/ui/sidebar-content/index.tsx';
import { Skeleton } from '@jira/navigation-apps__sidebar-nav4-sidebars-settings-global-common/src/ui/skeleton/index.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { lazyForPaint, type LazyComponent } from '@atlassian/react-loosely-lazy';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import { useOgTenantInfo } from '@jira/eoc__tenant-info/src/controllers/index.tsx';
import { Redirect } from '@atlassian/react-resource-router';

export const LazySidebarContent: LazyComponent<React.MemoExoticComponent<() => React.JSX.Element>> =
	lazyForPaint(
		() =>
			import(
				/* webpackChunkName: "async-jira-navigation-apps-sidebar-nav4-sidebar-global-settings-operations-settings" */ './main.tsx'
			),
		{
			includeInShellOnlySsrBundle: true,
			entryFsSsrLiftImportToModule: true,
		},
	);

const AsyncSidebar = (): React.JSX.Element => {
	const isAdmin = useIsAdmin();
	const { isOpsAdmin, shouldUseJSM } = useOgTenantInfo();

	const hasOperationsGeneralConfiguration = isAdmin || isOpsAdmin;

	if (!shouldUseJSM) {
		return <Redirect to="/jira/for-you" />;
	}

	if (!hasOperationsGeneralConfiguration) {
		return (
			<SidebarBoundary
				id="nav4.sidebar.global-settings.operations-settings"
				packageName="jiraNavigationAppsSidebarNav4GlobalSettingsOperationsSettings"
				teamName="alexstrasza"
			>
				<Redirect to="/jira/ops/overview" />
			</SidebarBoundary>
		);
	}

	return (
		<SidebarBoundary
			id="nav4.sidebar.global-settings.operations-settings"
			packageName="jiraNavigationAppsSidebarNav4GlobalSettingsOperationsSettings"
			teamName="alexstrasza"
		>
			<SidebarContent
				currentPageName="operations"
				// eslint-disable-next-line jira/integration/test-id-by-folder-structure
				testId="navigation-apps-sidebar-nav4-sidebars-settings-global-operations.ui.sidebar-content"
			>
				<Placeholder
					name="nav4.sidebar.global-settings.operations-settings"
					fallback={<Skeleton />}
				>
					<LazySidebarContent />
				</Placeholder>
			</SidebarContent>
		</SidebarBoundary>
	);
};

const ExportedComponent: React.MemoExoticComponent<() => React.JSX.Element> = memo(AsyncSidebar);
export default ExportedComponent;
